import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  CalendarPlus,
  CalendarDays,
  Star,
  Repeat,
  Pencil,
  Trash2,
  CheckCircle2,
  Mic,
  Loader2,
} from "lucide-react";
import { listBookings, deleteBooking } from "@/lib/api";
import BookingDialog from "@/components/BookingDialog";
import { toast } from "sonner";

function fmtWhen(iso) {
  if (!iso) return "No date set";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "No date set";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isUpcoming(iso, completed) {
  if (completed) return false;
  if (!iso) return false;
  return new Date(iso).getTime() >= Date.now();
}

export default function BookingsSection({ contactId, contactName, kind = "contact" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const list = await listBookings({ contact_id: contactId });
      setItems(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (b) => {
    setEditing(b);
    setDialogOpen(true);
  };
  const removeBooking = async (id) => {
    try {
      await deleteBooking(id);
      setItems((p) => p.filter((b) => b.id !== id));
      toast.success("Booking removed");
    } catch {
      toast.error("Failed to remove booking");
    }
  };

  const sorted = [...items].sort((a, b) => {
    const ta = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0;
    const tb = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
    return tb - ta;
  });

  return (
    <div className="space-y-3" data-testid="bookings-section">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-mono flex items-center gap-2">
          <CalendarDays className="h-3.5 w-3.5 text-[#00e5ff]" /> Bookings & journal
          {loading && <Loader2 className="h-3 w-3 animate-spin text-white/40" />}
        </div>
        <Button
          type="button"
          onClick={openNew}
          className="btn-neon h-9 px-3 text-xs"
          data-testid="bookings-new-btn"
        >
          <CalendarPlus className="h-4 w-4 mr-1.5" /> New booking
        </Button>
      </div>

      {sorted.length === 0 && !loading && (
        <div className="text-xs text-white/40 font-mono px-2 py-3 border border-dashed border-white/10 rounded-md">
          No bookings yet — tap “New booking” to plan a meet & journal it.
        </div>
      )}

      <div className="space-y-2">
        {sorted.map((b) => {
          const upcoming = isUpcoming(b.scheduled_at, b.completed);
          return (
            <div
              key={b.id}
              className={`booking-card ${upcoming ? "is-upcoming" : ""} ${b.completed ? "is-done" : ""}`}
              data-testid={`booking-card-${b.id}`}
            >
              <button
                type="button"
                onClick={() => openEdit(b)}
                className="flex-1 text-left min-w-0"
                data-testid={`booking-card-edit-${b.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display tracking-wide text-white text-base">
                    {b.title || "Booking"}
                  </span>
                  {b.repeat && (
                    <span className="chip chip-repeat" title="Repeat">
                      <Repeat className="h-3 w-3" /> repeat
                    </span>
                  )}
                  {b.completed && (
                    <span className="chip chip-done">
                      <CheckCircle2 className="h-3 w-3" /> done
                    </span>
                  )}
                  {upcoming && (
                    <span className="chip chip-upcoming">upcoming</span>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-white/55 font-mono">
                  {fmtWhen(b.scheduled_at)}
                </div>
                {(b.rating > 0 || (b.audio_clips || []).length > 0) && (
                  <div className="mt-2 flex items-center gap-3 text-xs text-white/50">
                    {b.rating > 0 && (
                      <span className="flex items-center gap-1 text-[#f5d300]">
                        {Array.from({ length: b.rating }).map((_, i) => (
                          <Star key={i} className="h-3.5 w-3.5" fill="currentColor" strokeWidth={1} />
                        ))}
                      </span>
                    )}
                    {(b.audio_clips || []).length > 0 && (
                      <span className="flex items-center gap-1 text-[#00e5ff]">
                        <Mic className="h-3.5 w-3.5" /> {b.audio_clips.length}
                      </span>
                    )}
                  </div>
                )}
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => openEdit(b)}
                  className="p-2 rounded-md text-white/55 hover:text-white hover:bg-white/5 transition"
                  aria-label="Edit booking"
                  data-testid={`booking-edit-${b.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeBooking(b.id)}
                  className="p-2 rounded-md text-white/55 hover:text-red-400 hover:bg-red-500/10 transition"
                  aria-label="Delete booking"
                  data-testid={`booking-delete-${b.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <BookingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contactId={contactId}
        contactName={contactName}
        kind={kind}
        booking={editing}
        onSaved={() => refresh()}
      />
    </div>
  );
}
